import { isWeb } from "@helpers/platform";
/* -----------------Globals--------------- */
import { memo, useState } from "react";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------Child components--------------- */
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import { images } from "@helpers/images";
/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
import Box from "@memorang/ui/src/components/Box";
import { FAB } from "@memorang/ui/src/components/FAB";
import type { IconType } from "components/types/icon";
/* -----------------UI--------------- */
import { Drawer } from "../../../CustomDrawer";

/* -----------------Types--------------- */
import { getShowStudyPackViews } from "@helpers/exams";
import useBreakpoints from "@hooks/useBreakpoints";
import useClerkUser from "@hooks/useClerkUser";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
/* -----------------Types--------------- */
import { DrawerContents, type Section } from "components/DrawerContents";
import type { Href } from "expo-router";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { QuickStudyButtonSkeleton } from "../components/Skeletons/QuickStudyButtonSkeleton";
import { drawerWidths } from "../constants";

const QuickStudyButton = memo(
	({
		isCollapsed,
		showStudyPackViews,
		toggleSessionConfigDialog,
	}: {
		isCollapsed: boolean;
		showStudyPackViews: boolean;
		toggleSessionConfigDialog: () => void;
	}) => {
		const isStudyPackEnabled = useAppStore(
			(store) => store.app.isStudyPackEnabled,
		);
		const {
			currentExamContext: { examId },
		} = useExamContext();
		const { handleSessionConfigNavigation } = useCustomNavigation();

		const { t } = useTranslation();
		if (!isStudyPackEnabled) {
			return null;
		}
		if (!examId) {
			return <QuickStudyButtonSkeleton />;
		}
		return (
			showStudyPackViews && (
				<Box paddingBottom={16} paddingTop={16} alignSelf="baseline">
					<FAB
						icon={(props) => <Icon name="play-outline" {...props} />}
						variant="primary"
						onPress={() => {
							handleSessionConfigNavigation(toggleSessionConfigDialog, {
								reset: "true",
							});
						}}
						label={isCollapsed ? "" : t("dashboard.header.quickStudy")}
						testID="quick-study-button"
					/>
				</Box>
			)
		);
	},
);
export const AppDrawer = () => {
	const appLinks = useAppStore((store) => store.app.links);
	const appLogo = useAppStore((store) => store.app.logo);
	const navigationRoutes = useAppStore((store) => store.navigation.routes.main);

	const showStudyPackAsHome = useAppStore((store) => store.showStudyPackAsHome);
	const store = useAppStore((store) => store.store);

	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);
	const { username } = useClerkUser();

	const showRedeem = useAppStore((store) => store.homeView?.showRedeem);
	const userAvatar = "";
	const {
		currentExamContext: { studyPackCollectionId, practiceTestProductNumericId },
	} = useExamContext();
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);
	const practiceTestEnabled = useAppStore(
		(state) => state.exam.practiceTest?.enabled,
	);
	const { t } = useTranslation();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const { smUp, mdDown } = useBreakpoints();
	const isCollapsed = smUp && mdDown;
	const leftDrawerWidth = isCollapsed
		? drawerWidths.collapsed
		: drawerWidths.expanded;
	const drawerType = smUp ? "permanent" : "slide";

	const sections: Section[] = [
		{
			title: "",
			items: navigationRoutes
				.filter((item) => {
					if (item.name === "profile") {
						return !isWeb;
					}

					if (item.name === "study-pack") {
						return (
							showStudyPackViews &&
							!showStudyPackAsHome &&
							studyPackCollectionId !== 38 &&
							studyPackCollectionId !== 20 &&
							item.enabled
						);
					}

					return item.enabled;
				})
				.map((item) => {
					return {
						label: t(`navbar.${item.name}`),
						focusedIcon: item.icon.selected as IconType,
						unfocusedIcon: item.icon.default as IconType,
						route: `/${item.name}` as Href,
						custom: item.custom,
					};
				}),
		},
	];

	const showPracticeTests =
		practiceTestProductNumericId &&
		(isWeb ? practiceTestEnabled.web : practiceTestEnabled.mobile);
	if (showPracticeTests) {
		sections[0].items.splice(1, 0, {
			label: practiceTestLabel ?? "Practice Tests",
			focusedIcon: "clipboard-check",
			unfocusedIcon: "clipboard-check-outline",
			route: "/practice-tests",
		});
	}

	if (
		showStudyPackViews &&
		!showStudyPackAsHome &&
		studyPackCollectionId !== 38 &&
		studyPackCollectionId !== 20 &&
		isWeb
	) {
		sections[0].items.splice(showPracticeTests ? 2 : 1, 0, {
			label: "Study-Pack",
			focusedIcon: "study-pack",
			unfocusedIcon: "study-pack-outline",
			route: "/study-pack",
			custom: true,
		});
	}

	if ((store.enabled || showRedeem) && isWeb) {
		sections[0].items.push({
			label: t("navbar.redeem"),
			focusedIcon: "gift",
			unfocusedIcon: "gift-outline",
			route: "/redeem?step=redeem",
		});
	}
	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog((prev) => !prev);
	};

	return (
		<>
			<Drawer
				drawerContent={(props) => (
					<ScrollView
						showsVerticalScrollIndicator={false}
						contentContainerStyle={{
							flexGrow: 1,
						}}
					>
						<DrawerContents
							drawerHeader={{
								appLogo: appLogo,
								renderFab: () => {
									return (
										<QuickStudyButton
											isCollapsed={isCollapsed}
											showStudyPackViews={showStudyPackViews}
											toggleSessionConfigDialog={toggleSessionConfigDialog}
										/>
									);
								},
							}}
							{...props}
							sections={sections}
							isCollapsed={isCollapsed}
							footer={{
								userName: username,
								userAvatar,
								storeDownloadBadge: {
									ios: {
										badge: images.appStores.ios,
										href: appLinks.ios,
									},
									android: {
										badge: images.appStores.android,
										href: appLinks.android,
									},
								},
							}}
						/>
					</ScrollView>
				)}
				screenOptions={{
					drawerType: drawerType,
					headerTitle: "",
					drawerStyle: {
						width: leftDrawerWidth,
						borderRightColor: "transparent",
					},
					header: () => null,
				}}
			/>
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
		</>
	);
};
